"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const filter_1 = __importDefault(require("lodash/filter"));
const findIndex_1 = __importDefault(require("lodash/findIndex"));
const get_1 = __importDefault(require("lodash/get"));
const map_1 = __importDefault(require("lodash/map"));
const sortBy_1 = __importDefault(require("lodash/sortBy"));
const GREGORIAN_ALL_WEEK_NAME_1 = __importDefault(require("../../../../constants/date/GREGORIAN_ALL_WEEK_NAME"));
const GREGORIAN_CLOSED_DAY_1 = __importDefault(require("../../../../constants/date/GREGORIAN_CLOSED_DAY"));
const GREGORIAN_DAY_NAME_MAP_1 = __importDefault(require("../../../../constants/date/GREGORIAN_DAY_NAME_MAP"));
const TWENTY_FOUR_HOURS_1 = __importDefault(require("../../../../constants/date/TWENTY_FOUR_HOURS"));
const formatTime_1 = __importDefault(require("../formatTime"));
/**
 * Generates Open Day sequence based on time and sequence index
 * @param {Array} arr
 * @returns {Array[Array]}
 */
const sequenceOpenDays = (arr) => {
    var _a, _b, _c, _d, _e, _f;
    let index = arr.length > 0 ? arr[0].sortKey : -1;
    let prevTime = arr.length > 0 ? arr[0].time : '';
    let preTimeRangeSize = arr.length > 0 ? (_b = (_a = arr[0]) === null || _a === void 0 ? void 0 : _a.timeRanges) === null || _b === void 0 ? void 0 : _b.length : 0;
    let temp = [];
    const res = [];
    // eslint-disable-next-line no-loops/no-loops
    for (let i = 0; i < arr.length; i++) {
        // if the days are in a sequence
        // and if the time matches
        if (arr[i].sortKey === index &&
            prevTime === arr[i].time &&
            preTimeRangeSize === ((_d = (_c = arr[i]) === null || _c === void 0 ? void 0 : _c.timeRanges) === null || _d === void 0 ? void 0 : _d.length)) {
            temp.push(arr[i]);
        }
        else {
            res.push(temp); // previous sequence is broken
            temp = [arr[i]];
        }
        // update the counters
        index = arr[i].sortKey + 1;
        prevTime = arr[i].time;
        preTimeRangeSize = (_f = (_e = arr[i]) === null || _e === void 0 ? void 0 : _e.timeRanges) === null || _f === void 0 ? void 0 : _f.length;
        // end of loop
        if (i === arr.length - 1) {
            res.push(temp);
        }
    }
    return res;
};
/**
 * Generates Closed Day sequence based on sequence index only
 * @param {Array} arr
 * @returns {Array[Array]}
 */
const sequenceClosedDays = (arr) => {
    let index = arr.length > 0 ? arr[0].sortKey : -1;
    let temp = [];
    const res = [];
    // eslint-disable-next-line no-loops/no-loops
    for (let i = 0; i < arr.length; i++) {
        // if the days are in a sequence
        if (arr[i].sortKey === index) {
            temp.push(arr[i]);
        }
        else {
            res.push(temp); // previous sequence is broken
            temp = [arr[i]];
        }
        index = arr[i].sortKey + 1;
        // end of loop
        if (i === arr.length - 1) {
            res.push(temp);
        }
    }
    return res;
};
/**
 * Formats open day sequence
 * @param {Array[Array]} arr
 * @returns {Array}
 */
const formatOpenDaySequence = (arr) => {
    return (0, map_1.default)(arr, (item) => {
        const is24hr = item[0].start === item[0].end;
        const timeRanges = (0, map_1.default)(item[0].timeRanges, (time) => {
            const splitedTime = time.split('-');
            return `${(0, formatTime_1.default)(splitedTime[0])} - ${(0, formatTime_1.default)(splitedTime[1])}`;
        });
        // for a single day
        if (item.length === 1) {
            return {
                dayLabel: item[0].day,
                timeLabel: is24hr
                    ? TWENTY_FOUR_HOURS_1.default
                    : `${(0, formatTime_1.default)(item[0].start)} - ${(0, formatTime_1.default)(item[0].end)}`,
                sortKey: item[0].sortKey,
                start: item[0].start,
                end: item[0].end,
                timeRanges,
            };
        }
        const daysLabels = (0, map_1.default)(item, (el) => el.day);
        // for a group of days in a sequence
        const tempDayLabel = `${item[0].day} - ${item[item.length - 1].day}`;
        // find if all week
        const isAllWeek = tempDayLabel.toLowerCase() ===
            `${GREGORIAN_DAY_NAME_MAP_1.default.SATURDAY} - ${GREGORIAN_DAY_NAME_MAP_1.default.FRIDAY}`; // All week
        const dayLabel = isAllWeek ? GREGORIAN_ALL_WEEK_NAME_1.default.EN[0] : tempDayLabel;
        return {
            dayLabel,
            daysLabels,
            timeLabel: is24hr
                ? TWENTY_FOUR_HOURS_1.default
                : `${(0, formatTime_1.default)(item[0].start)} - ${(0, formatTime_1.default)(item[0].end)}`,
            sortKey: item[0].sortKey,
            start: item[0].start,
            end: item[0].end,
            timeRanges,
        };
    });
};
/**
 * Formats closed day sequence
 * @param {Array[Array]} arr
 * @returns {Array}
 */
const formatClosedDaySequence = (arr) => {
    return (0, map_1.default)(arr, (item) => {
        // for a single day
        if (item.length === 1) {
            return {
                dayLabel: item[0].day,
                timeLabel: GREGORIAN_CLOSED_DAY_1.default.EN[0],
                sortKey: item[0].sortKey,
            };
        }
        const daysLabels = (0, map_1.default)(item, (el) => el.day);
        // for a group of days in a sequence
        const tempDayLabel = `${item[0].day} - ${item[item.length - 1].day}`;
        // find if all week
        const isAllWeek = tempDayLabel.toLowerCase() ===
            `${GREGORIAN_DAY_NAME_MAP_1.default.SATURDAY} - ${GREGORIAN_DAY_NAME_MAP_1.default.FRIDAY}`; // All week
        const dayLabel = isAllWeek ? GREGORIAN_ALL_WEEK_NAME_1.default.EN[0] : tempDayLabel;
        return {
            dayLabel,
            daysLabels,
            timeLabel: GREGORIAN_CLOSED_DAY_1.default.EN[0],
            sortKey: item[0].sortKey,
        };
    });
};
/**
 * Generates formatted timing summary
 * @param {Array} timings
 * @returns {Array}
 */
const generateSummary = (timings) => {
    // format the raw timings into usable format
    let mapTimings = (0, map_1.default)(timings, (item) => {
        var _a;
        // closed day
        if (!item.open) {
            return {
                day: item.day.toLowerCase(),
                time: null,
                closed: true,
            };
        }
        // initial value to not add timeranges to the returned object
        let is24hr = true;
        const ismultiTimeRange = ((_a = item.timeRanges) === null || _a === void 0 ? void 0 : _a.length) > 1;
        const startWorkingHour = (0, get_1.default)(item.timeRanges[0], 'start');
        const lastWorkingHour = (0, get_1.default)(item.timeRanges[item.timeRanges.length - 1], 'end');
        is24hr =
            !ismultiTimeRange &&
                item.timeRanges.length > 0 &&
                startWorkingHour === lastWorkingHour;
        const timeRanges = (0, map_1.default)(item.timeRanges, (el) => {
            return `${el.start}-${el.end}`;
        });
        // open day
        return Object.assign({ day: item.day.toLowerCase(), time: `${startWorkingHour}-${lastWorkingHour}`, closed: false, start: startWorkingHour, end: lastWorkingHour }, (!is24hr && { timeRanges }));
    });
    // find index for saturday
    const id = (0, findIndex_1.default)(mapTimings, (o) => {
        return o.day.toLowerCase() === GREGORIAN_DAY_NAME_MAP_1.default.SATURDAY; // saturday
    });
    // remove saturday from timings array
    const removedArr = mapTimings.splice(id, 1);
    // add saturday to front
    mapTimings.unshift(removedArr[0]);
    // add sort key
    mapTimings = (0, map_1.default)(mapTimings, (item, index) => {
        return Object.assign(Object.assign({}, item), { sortKey: index });
    });
    // filter closed days
    const cDays = (0, filter_1.default)(mapTimings, 'closed');
    // filter open days
    const oDays = (0, filter_1.default)(mapTimings, (o) => {
        return !o.closed;
    });
    // sequence
    const oDaysSequence = sequenceOpenDays(oDays);
    const cDaysSequence = sequenceClosedDays(cDays);
    //  format open and closed days sequence
    const formattedSeqODays = formatOpenDaySequence(oDaysSequence);
    const formattedSeqCDays = formatClosedDaySequence(cDaysSequence);
    // merge closed and open day sequences
    const mergedArr = [...formattedSeqCDays, ...formattedSeqODays];
    // sort the sequence based on sortKey
    return (0, sortBy_1.default)(mergedArr, 'sortKey');
};
/**
 * Formats branch timing summary to required format
 * @package {Array} branch timings
 * @returns {Array}
 */
const getTimingSummary = (timings) => {
    const summary = generateSummary(timings);
    return summary;
};
exports.default = getTimingSummary;
